/*
 * The forced password reset (API-15, flows/admin-auth). Reached only with a must-reset token: the
 * bootstrap SUPER_ADMIN and anyone an admin has reset land here and cannot pass until they set a new
 * password. On success the re-issued token (flag cleared) is adopted and the operator lands on
 * Contests. A token-less visitor is sent to login; an operator who no longer owes a reset is sent to
 * the landing page. The new password is confirmed client-side and length-checked to the API bound.
 */
import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Banner, Button, Card, Input } from '@cou/admin-ui';
import { useAdminAuth } from '../session/admin-auth-context';
import { resetPassword as defaultResetPassword } from '../auth/admin-auth-client';
import { refusalMessage } from '../auth/refusal-copy';
import { AuthLayout } from './AuthLayout';
import { LANDING_SECTION } from '../shell/nav-sections';

const MIN_PASSWORD_LENGTH = 8;

export interface ResetPasswordScreenProps {
  /** Injected for tests; defaults to the real API-15 reset call. */
  readonly resetPassword?: typeof defaultResetPassword;
}

export function ResetPasswordScreen({
  resetPassword = defaultResetPassword,
}: ResetPasswordScreenProps = {}) {
  const { claims, getToken, signIn } = useAdminAuth();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (claims === null) {
    return <Navigate to="/login" replace />;
  }
  if (!claims.mustResetPw) {
    return <Navigate to={LANDING_SECTION.path} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`Choose a password of at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirm) {
      setError('The passwords do not match.');
      return;
    }
    const token = getToken();
    if (token === null) {
      setError(refusalMessage(undefined));
      return;
    }
    setSubmitting(true);
    try {
      const result = await resetPassword(token, newPassword);
      if (result.ok) {
        signIn(result.adminToken);
        void navigate(LANDING_SECTION.path, { replace: true });
      } else {
        setError(refusalMessage(result.code));
      }
    } catch {
      setError(refusalMessage(undefined));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <Card variant="frame" eyebrow="Caesars Over/Under" title="Set a new password">
        <p className="font-ui text-[13px] leading-[1.5] text-ink-3">
          Your account requires a new password before you can continue.
        </p>
        <form className="flex flex-col gap-4" onSubmit={(event) => void onSubmit(event)} noValidate>
          {error && (
            <Banner variant="danger" role="alert">
              {error}
            </Banner>
          )}
          <Input
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
          <Input
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Set password and continue'}
          </Button>
        </form>
      </Card>
    </AuthLayout>
  );
}
